/**
 * Staff — éditeur de l'arbre de navigation menu.
 *
 * Vue plate indentée par niveau (pas de drag&drop V1 : boutons ↑↓ + select
 * parent dans la modal d'édition). Empty state propose le seed bootstrap
 * depuis les catégories POS détectées au runtime.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './staff-design-system.css';
import './Staff.css';
import './StaffCarteNavigation.css';
import { EditNavigationNodeSheet, type NavigationNodeInput, type PosCategoryRef } from '../../components/staff/EditNavigationNodeSheet';
import { SortableNavTree, type NavNode } from '../../components/staff/SortableNavTree';
import { isCurrentUserAdmin } from '../../utils/auth';
import { apiFetch } from '../../utils/api';

interface AdminPayload {
  roots: any[];
  unmappedCategories: PosCategoryRef[];
  nodes: NavigationNodeInput[];
  posCategories: PosCategoryRef[];
}

type ModalState =
  | { mode: 'create'; parentId: string | null; suggestedPosition?: number }
  | { mode: 'edit'; node: NavigationNodeInput }
  | null;

export function StaffCarteNavigation() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState<AdminPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [seeding, setSeeding] = useState(false);
  const admin = useMemo(() => isCurrentUserAdmin(), []);

  const fetchPayload = useCallback(async () => {
    setError(null);
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) {
      setError('Non connecté');
      setLoading(false);
      return;
    }
    try {
      const res = await apiFetch(`/pos/navigation/admin`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: AdminPayload = await res.json();
      setPayload(data);
    } catch (err: any) {
      setError(err?.message ?? 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchPayload(); }, [fetchPayload]);

  const seed = useCallback(async (force = false) => {
    if (seeding) return;
    setSeeding(true);
    try {
      await apiFetch(`/pos/navigation/seed${force ? '?force=true' : ''}`, {
        method: 'POST',
      });
      await fetchPayload();
    } finally {
      setSeeding(false);
    }
  }, [seeding, fetchPayload]);

  // Réordonnancement drag-drop (par parent) : 1 PATCH atomique au lieu des
  // anciens swaps ↑↓ (2 PUT + refetch par pas). Optimistic dans SortableNavTree.
  const reorderNodes = useCallback(async (_parentId: string | null, orderedIds: string[]) => {
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) return;
    try {
      const r = await apiFetch(`/pos/navigation/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: orderedIds }),
      });
      if (!r.ok) throw new Error('reorder failed');
      void fetchPayload(); // sync (ordre identique à l'optimistic → pas de flash)
    } catch {
      void fetchPayload(); // rollback : ré-affiche l'ordre serveur
    }
  }, [fetchPayload]);

  const deleteNode = useCallback(async (nodeId: string) => {
    const ok = window.confirm('Supprimer ce nœud et tous ses enfants ?');
    if (!ok) return;
    await apiFetch(`/pos/navigation/nodes/${nodeId}`, {
      method: 'DELETE',
    });
    await fetchPayload();
  }, [fetchPayload]);

  if (!admin) {
    return (
      <div className="staff-page-v2">
        <div className="staff-header-v2">
          <button className="staff-back-v2" onClick={() => navigate('/staff/dashboard')}>‹</button>
          <h1>Navigation</h1>
        </div>
        <p className="staff-empty">Accès staff uniquement.</p>
      </div>
    );
  }

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/staff/carte')}>‹</button>
        <h1>Navigation</h1>
        {payload && payload.nodes.length > 0 && (
          <span className="staff-badge-v2">
            {payload.nodes.length} nœud{payload.nodes.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading && <p className="staff-empty">Chargement…</p>}
      {error && <p className="staff-empty" style={{ color: 'var(--color-error)' }}>{error}</p>}

      {!loading && !error && payload && payload.nodes.length === 0 && (
        <div className="nav-empty">
          <p className="nav-empty-text">
            Aucune navigation configurée. Importer depuis les catégories de la caisse
            ({payload.posCategories.length} disponibles) pour démarrer.
          </p>
          <button className="nav-empty-cta" onClick={() => seed(false)} disabled={seeding}>
            {seeding ? 'Import en cours…' : '📋 Importer depuis Cashpad'}
          </button>
        </div>
      )}

      {!loading && !error && payload && payload.nodes.length > 0 && (
        <>
          <p className="nav-dnd-hint">Appui long sur une carte pour la déplacer (réordonner dans sa catégorie).</p>
          <div className="nav-tree">
            <SortableNavTree
              nodes={payload.nodes as unknown as NavNode[]}
              posCategories={payload.posCategories}
              onReorder={reorderNodes}
              onEdit={(node) => setModal({ mode: 'edit', node: node as unknown as NavigationNodeInput })}
              onDelete={deleteNode}
              onAddChild={(parentId) => setModal({ mode: 'create', parentId })}
            />
          </div>

          <div className="nav-add-row">
            <button
              className="nav-add-btn"
              onClick={() => setModal({ mode: 'create', parentId: null })}
            >+ Nouveau nœud racine</button>
          </div>

          {payload.unmappedCategories.length > 0 && (
            <div className="nav-unmapped">
              <h3>Catégories POS non classées ({payload.unmappedCategories.length})</h3>
              <p className="nav-unmapped-hint">
                Ces catégories de la caisse n'apparaissent dans aucun nœud — leurs produits
                ne sont pas visibles côté client.
              </p>
              <ul>
                {payload.unmappedCategories.map((c) => (
                  <li key={`${c.provider}::${c.categoryId}`}>
                    <span>{c.categoryName}</span>
                    <span className="nav-unmapped-count">({c.productCount} produits)</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <EditNavigationNodeSheet
        state={modal}
        allNodes={payload?.nodes ?? []}
        posCategories={payload?.posCategories ?? []}
        onClose={() => setModal(null)}
        onSaved={fetchPayload}
      />
    </div>
  );
}
